import React, { useEffect, useState } from "react";
import { X, GripVertical } from "lucide-react";

const TableColumnManagerModal = ({
  isOpen,
  onClose,
  allDataHeaders = [],
  visibleDataHeaders = [],
  onApply,
}) => {
  const [draft, setDraft] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    const visibleSet = new Set(visibleDataHeaders);
    const ordered = [
      ...visibleDataHeaders,
      ...allDataHeaders.filter((h) => !visibleSet.has(h)),
    ];
    setDraft(ordered.map((key) => ({ key, visible: visibleSet.has(key) })));
  }, [isOpen, allDataHeaders, visibleDataHeaders]);

  if (!isOpen) return null;

  const move = (index, direction) => {
    const next = [...draft];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft(next);
  };

  const toggle = (index) => {
    setDraft((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, visible: !item.visible } : item
      )
    );
  };

  const handleApply = () => {
    const visible = draft.filter((d) => d.visible).map((d) => d.key);
    onApply?.(visible);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-acs-dark-surface">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-acs-dark-border">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-acs-dark-text">
            Manage columns
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[360px] overflow-y-auto px-4 py-3">
          {draft.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
              No columns available.
            </p>
          ) : (
            <ul className="space-y-1">
              {draft.map((item, index) => (
                <li
                  key={item.key}
                  className="flex items-center gap-2 rounded border border-gray-100 px-2 py-1.5 dark:border-acs-dark-border"
                >
                  <GripVertical size={14} className="text-gray-400 shrink-0" />
                  <label className="flex flex-1 cursor-pointer items-center gap-2 text-sm text-gray-800 dark:text-acs-dark-text">
                    <input
                      type="checkbox"
                      checked={item.visible}
                      onChange={() => toggle(index)}
                      className="rounded border-gray-300"
                    />
                    <span>{item.key}</span>
                  </label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-acs-dark-hover"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={index === draft.length - 1}
                      className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-acs-dark-hover"
                    >
                      ↓
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3 dark:border-acs-dark-border">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:text-acs-dark-text dark:hover:bg-acs-dark-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="rounded-full bg-[#223688] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#1a2b6d]"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default TableColumnManagerModal;
